import { z } from 'zod';
import { IsoDate, IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * WS9 — discounts & promo codes (brief 09 §3).
 *
 * TWO distinct things, deliberately kept apart:
 *
 *  1. A DISCOUNT applied at CHECKOUT (till): a percent or fixed-amount reduction against a supply,
 *     ALWAYS carrying an ACTOR (staffId) + a REASON CODE, recorded in an append-only audit trail.
 *     There are NO silent price overrides (brief non-negotiable): a discount is a `discount`
 *     `ReceiptLineKind` line that reduces the taxable consideration of the supply the normal vat.ts
 *     way, and every one is reportable (reason codes are queryable). The discount math here is PURE
 *     (computeDiscount) and the RESULT (an integer-pence reduction) is what the till applies.
 *
 *  2. A PROMO CODE for ONLINE BOOKING (WS8): a salon-defined code with a percent/fixed value, a usage
 *     limit, a valid-from/until window, and an optional service scope. Redeemable through the public
 *     booking flow via a resolve/validate seam — it does NOT touch the WS8 frozen-policy contract.
 *
 * ADR-0008 §4 boundary (kept clean vs the vouchers feature): a "£X off" PURE-DISCOUNT voucher carries
 * NO ledger balance and is a discount LINE here, NOT a voucher_ledger posting. Discounts never post to
 * gift_card_liability — that is the redeemable-balance path, which lives in the vouchers module.
 *
 * Money is integer pence; percent values are basis points (2000 = 20.00%).
 */

// ── Shared enums ──────────────────────────────────────────────────────────────

/** A discount is either a PERCENT of the supply (basis points) or a FIXED amount in pence. */
export const DiscountType = z.enum(['percent', 'fixed']);
export type DiscountType = z.infer<typeof DiscountType>;

/**
 * The canonical reason codes a checkout discount MUST carry (the audit trail — no silent overrides).
 * `other` always requires a free-text `reasonNote`. The set is deliberately small + reportable; the
 * console renders these as the discount-reason picker on the till.
 */
export const DiscountReasonCode = z.enum([
  'loyalty',
  // WS-13 Feature 3b — the recurring-membership benefit: while a client_membership is active the till
  // applies the frozen benefit_discount_bps as a standard `discount` line carrying this reason code.
  // reason_code has NO DB CHECK (Zod-only), so adding it here is the whole change — the member benefit
  // is a pure discount that reduces taxable consideration the normal vat.ts way (ADR-0008 §4), never a
  // ledger posting.
  'membership',
  'goodwill',
  'staff_friends_family',
  'price_match',
  'manager_comp',
  'promotion',
  'first_visit',
  'student',
  'senior',
  'other',
]);
export type DiscountReasonCode = z.infer<typeof DiscountReasonCode>;

// ── Pure discount math (the result the till applies) ───────────────────────────

/**
 * The computed reduction for a discount against a base (the supply's gross, VAT-inclusive). The till
 * applies `discountPence` as a `discount` receipt line; `netPence` is the supply after the reduction
 * (never below 0). A percent discount is half-up rounded; a fixed discount is clamped to the base.
 */
export const DiscountComputation = z
  .object({
    type: DiscountType,
    /** The percent (bps) or fixed (pence) input that produced the result. */
    valueBps: z.int().min(0).max(10000).nullable(),
    valuePence: Pence.nullable(),
    basePence: Pence.describe('the supply gross the discount applied to (VAT-inclusive)'),
    discountPence: Pence.describe(
      'the integer-pence reduction (clamped to the base, never > base)',
    ),
    netPence: Pence.describe('basePence − discountPence (never below 0)'),
  })
  .meta({ id: 'DiscountComputation' });
export type DiscountComputation = z.infer<typeof DiscountComputation>;

// ── Checkout discount (the append-only audit trail entity) ─────────────────────

/**
 * One applied checkout discount — the append-only audit record. A correction is a NEW reversing
 * record, never an edit (mirrors the vouchers ledger discipline). Renter-aware-floor-visible like
 * payments: a renter sees discounts in its own book; the owner/manager reconciles the floor.
 */
export const Discount = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable().describe('the book the discount was applied in; null for the floor'),
    paymentId: Uuid.nullable().describe(
      'the till payment the discount was applied against, if any',
    ),
    receiptId: Uuid.nullable(),
    appointmentId: Uuid.nullable(),
    type: DiscountType,
    valueBps: z.int().min(0).max(10000).nullable(),
    valuePence: Pence.nullable(),
    basePence: Pence,
    discountPence: Pence.describe('the integer-pence reduction actually applied'),
    reasonCode: DiscountReasonCode.describe('the reportable reason (no silent overrides)'),
    reasonNote: z
      .string()
      .nullable()
      .describe('free-text detail; required when reasonCode = other'),
    /** Set when the discount came from a redeemed online-booking promo code. */
    promoCodeId: Uuid.nullable(),
    appliedByStaffId: Uuid.nullable().describe('the ACTOR — who applied it (audit trail)'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Discount' });
export type Discount = z.infer<typeof Discount>;

/**
 * Apply a discount at checkout. The actor is the authenticated staff member (server-resolved, never
 * client-supplied). `basePence` is the supply gross the discount reduces. Exactly one of `valueBps`
 * (percent) / `valuePence` (fixed) is supplied, matching `type`. Idempotent on `idempotencyKey`.
 */
export const ApplyDiscount = z
  .object({
    paymentId: Uuid.nullish().describe('the till payment this discount is applied against, if any'),
    appointmentId: Uuid.nullish(),
    clientId: Uuid.nullish(),
    type: DiscountType,
    /** Percent discount in basis points (required when type = percent). */
    valueBps: z.int().min(1).max(10000).optional(),
    /** Fixed discount in pence (required when type = fixed). */
    valuePence: Pence.optional(),
    basePence: Pence.describe('the supply gross the discount reduces (VAT-inclusive)'),
    reasonCode: DiscountReasonCode,
    reasonNote: z.string().trim().min(1).max(500).optional(),
    idempotencyKey: z.string().min(8).max(200),
  })
  .refine((b) => (b.type === 'percent' ? b.valueBps != null : b.valuePence != null), {
    message: 'percent discounts require valueBps; fixed discounts require valuePence',
    path: ['type'],
  })
  .refine((b) => b.reasonCode !== 'other' || Boolean(b.reasonNote), {
    message: 'reasonNote is required when reasonCode is "other" (no silent overrides)',
    path: ['reasonNote'],
  })
  .meta({ id: 'ApplyDiscount' });
export type ApplyDiscount = z.infer<typeof ApplyDiscount>;

/** Filter the discount audit trail (reportable: reason codes are queryable). */
export const ListDiscountsQuery = z.object({
  reasonCode: DiscountReasonCode.optional(),
  paymentId: Uuid.optional(),
  from: IsoDateTime.optional(),
  to: IsoDateTime.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListDiscountsQuery = z.infer<typeof ListDiscountsQuery>;

/**
 * The discount-reason report (reportable, never silent). Aggregates applied discounts by reason code
 * so an owner can see WHERE margin is going — the brief's "reportable" requirement made concrete.
 */
export const DiscountReasonSummary = z
  .object({
    reasonCode: DiscountReasonCode,
    count: z.int().min(0),
    totalDiscountPence: Pence,
  })
  .meta({ id: 'DiscountReasonSummary' });
export type DiscountReasonSummary = z.infer<typeof DiscountReasonSummary>;

export const DiscountReport = z
  .object({
    totalDiscountPence: Pence,
    discountCount: z.int().min(0),
    byReason: z.array(DiscountReasonSummary),
    generatedAt: IsoDateTime,
  })
  .meta({ id: 'DiscountReport' });
export type DiscountReport = z.infer<typeof DiscountReport>;

// ── Promo codes (online booking) ───────────────────────────────────────────────

const PromoCodeString = z
  .string()
  .trim()
  .min(3)
  .max(40)
  .regex(/^[A-Za-z0-9_-]+$/, 'letters, digits, hyphen or underscore only');

/**
 * A salon-defined promo code redeemable through the public online-booking flow. `usageLimit` null =
 * unlimited; `validFrom`/`validUntil` null = open-ended. `serviceId` null = applies to any service.
 * `timesUsed` is DERIVED (the count of redemptions), never a mutable column, so it can never drift.
 */
export const PromoCode = z
  .object({
    id: Uuid,
    salonId: Uuid,
    code: z.string().describe('the case-insensitive code clients type (stored upper-cased)'),
    type: DiscountType,
    valueBps: z.int().min(1).max(10000).nullable(),
    valuePence: Pence.nullable(),
    /** Null = applies to any service; set = scoped to one service only. */
    serviceId: Uuid.nullable(),
    /** Null = unlimited total redemptions. */
    usageLimit: z.int().min(1).nullable(),
    /** DERIVED count of redemptions (never a stored column — cannot drift). */
    timesUsed: z.int().min(0),
    validFrom: IsoDate.nullable(),
    validUntil: IsoDate.nullable(),
    active: z.boolean(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'PromoCode' });
export type PromoCode = z.infer<typeof PromoCode>;

export const CreatePromoCode = z
  .object({
    code: PromoCodeString,
    type: DiscountType,
    valueBps: z.int().min(1).max(10000).optional(),
    valuePence: Pence.optional(),
    serviceId: Uuid.nullish(),
    usageLimit: z.int().min(1).max(1_000_000).nullish(),
    validFrom: IsoDate.nullish(),
    validUntil: IsoDate.nullish(),
  })
  .refine((b) => (b.type === 'percent' ? b.valueBps != null : b.valuePence != null), {
    message: 'percent promo codes require valueBps; fixed promo codes require valuePence',
    path: ['type'],
  })
  .refine((b) => !b.validFrom || !b.validUntil || b.validFrom <= b.validUntil, {
    message: 'validFrom must be on or before validUntil',
    path: ['validUntil'],
  })
  .meta({ id: 'CreatePromoCode' });
export type CreatePromoCode = z.infer<typeof CreatePromoCode>;

export const UpdatePromoCode = z
  .object({
    usageLimit: z.int().min(1).max(1_000_000).nullish(),
    validFrom: IsoDate.nullish(),
    validUntil: IsoDate.nullish(),
    active: z.boolean().optional(),
  })
  .meta({ id: 'UpdatePromoCode' });
export type UpdatePromoCode = z.infer<typeof UpdatePromoCode>;

/**
 * The result of resolving a promo code against a (service, base price) at the public booking surface.
 * `valid` tells the page whether the code applies; when valid, `discountPence`/`netPence` are the
 * deterministic, shown-before-commitment reduction (same honesty rule as brief-07 policies). When not
 * valid, `reason` names why (expired/usage-limit/scope/unknown) so the page can message the client.
 */
export const PromoResolution = z
  .object({
    code: z.string(),
    valid: z.boolean(),
    reason: z
      .enum(['ok', 'unknown', 'inactive', 'not_started', 'expired', 'usage_limit', 'wrong_service'])
      .describe('ok when valid; otherwise the disqualifying reason'),
    type: DiscountType.nullable(),
    basePence: Pence,
    discountPence: Pence.describe('the reduction if valid, else 0'),
    netPence: Pence.describe('basePence − discountPence (never below 0)'),
    promoCodeId: Uuid.nullable(),
  })
  .meta({ id: 'PromoResolution' });
export type PromoResolution = z.infer<typeof PromoResolution>;
